/**
 * Page d'administration : gestion des squads.
 */

import React, { useEffect, useState } from "react";
import { getSquads, createSquad, updateSquad, deleteSquad, getUsers } from "../api/adminApi";
import ConfirmDialog from "../components/ConfirmDialog";
import AdminNav from "../components/AdminNav";
import "./PageStyles.css";

export default function AdminSquadListPage() {
  const [squads, setSquads] = useState([]);
  const [users, setUsers] = useState([]);
  const [deleteId, setDeleteId] = useState(null);
  const [editSquad, setEditSquad] = useState(null); // null | { id?, name, member_ids, lead_ids }
  const [success, setSuccess] = useState("");

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    const [squadsData, usersData] = await Promise.all([getSquads(), getUsers()]);
    setSquads(squadsData);
    setUsers(usersData);
  }

  async function handleDelete() {
    await deleteSquad(deleteId);
    setDeleteId(null);
    setSuccess("Squad supprimée.");
    setTimeout(() => setSuccess(""), 3000);
    loadData();
  }

  async function handleSave() {
    if (!editSquad.name.trim()) return;
    if (editSquad.id) {
      await updateSquad(editSquad.id, {
        name: editSquad.name,
        member_ids: editSquad.member_ids,
        lead_ids: editSquad.lead_ids,
      });
    } else {
      await createSquad({
        name: editSquad.name,
        member_ids: editSquad.member_ids,
        lead_ids: editSquad.lead_ids,
      });
    }
    setEditSquad(null);
    setSuccess(editSquad.id ? "Squad mise à jour." : "Squad créée.");
    setTimeout(() => setSuccess(""), 3000);
    loadData();
  }

  function openCreate() {
    setEditSquad({ name: "", member_ids: [], lead_ids: [] });
  }

  function openEdit(squad) {
    setEditSquad({
      id: squad.id,
      name: squad.name,
      member_ids: [...squad.member_ids],
      lead_ids: [...squad.lead_ids],
    });
  }

  function toggleMember(userId) {
    if (!editSquad) return;
    const ids = editSquad.member_ids.includes(userId)
      ? editSquad.member_ids.filter((id) => id !== userId)
      : [...editSquad.member_ids, userId];
    setEditSquad({ ...editSquad, member_ids: ids });
  }

  function toggleLead(userId) {
    if (!editSquad) return;
    const ids = editSquad.lead_ids.includes(userId)
      ? editSquad.lead_ids.filter((id) => id !== userId)
      : [...editSquad.lead_ids, userId];
    setEditSquad({ ...editSquad, lead_ids: ids });
  }

  function getUserName(userId) {
    const u = users.find((u) => u.id === userId);
    return u ? `${u.first_name} ${u.last_name}` : `#${userId}`;
  }

  return (
    <div className="page">
      <AdminNav />
      <div className="page-header">
        <h2>Squads</h2>
        <button className="btn btn-primary" onClick={openCreate}>
          Nouvelle squad
        </button>
      </div>
      {success && <div className="success-message">{success}</div>}

      {squads.length === 0 ? (
        <p style={{ color: "#64748b", textAlign: "center", padding: "2rem" }}>
          Aucune squad configurée.
        </p>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
          {squads.map((squad) => (
            <div key={squad.id} className="form-card" style={{ margin: 0 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.5rem" }}>
                <h3 style={{ margin: 0, fontSize: "1.1rem" }}>{squad.name}</h3>
                <div style={{ display: "flex", gap: "0.5rem" }}>
                  <button className="btn btn-small" onClick={() => openEdit(squad)}>
                    Modifier
                  </button>
                  <button className="btn btn-small btn-danger" onClick={() => setDeleteId(squad.id)}>
                    Supprimer
                  </button>
                </div>
              </div>
              <div style={{ fontSize: "0.9rem", color: "#475569" }}>
                <div style={{ marginBottom: "0.25rem" }}>
                  <strong>Leads :</strong>{" "}
                  {squad.lead_ids.length > 0
                    ? squad.lead_ids.map((id) => getUserName(id)).join(", ")
                    : "—"}
                </div>
                <div>
                  <strong>Membres :</strong>{" "}
                  {squad.member_ids.length > 0
                    ? squad.member_ids.map((id) => getUserName(id)).join(", ")
                    : "—"}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {deleteId && (
        <ConfirmDialog
          message="Supprimer cette squad ?"
          onConfirm={handleDelete}
          onCancel={() => setDeleteId(null)}
        />
      )}

      {editSquad && (
        <div className="confirm-overlay">
          <div className="confirm-box" style={{ maxWidth: "550px" }}>
            <h3 style={{ marginTop: 0 }}>
              {editSquad.id ? "Modifier la squad" : "Nouvelle squad"}
            </h3>
            <div className="form-group">
              <label>Nom de la squad</label>
              <input
                type="text"
                value={editSquad.name}
                onChange={(e) => setEditSquad({ ...editSquad, name: e.target.value })}
                autoFocus
              />
            </div>
            <div style={{ display: "flex", gap: "1.5rem" }}>
              <div style={{ flex: 1 }}>
                <p style={{ fontWeight: 600, fontSize: "0.9rem", marginBottom: "0.5rem" }}>
                  Leads
                </p>
                <div style={{ maxHeight: "250px", overflow: "auto" }}>
                  {users.map((u) => (
                    <label key={u.id} style={{ display: "flex", alignItems: "center", gap: "0.5rem", padding: "0.3rem 0", fontSize: "0.9rem", cursor: "pointer" }}>
                      <input
                        type="checkbox"
                        checked={editSquad.lead_ids.includes(u.id)}
                        onChange={() => toggleLead(u.id)}
                      />
                      {u.first_name} {u.last_name}
                    </label>
                  ))}
                </div>
              </div>
              <div style={{ flex: 1 }}>
                <p style={{ fontWeight: 600, fontSize: "0.9rem", marginBottom: "0.5rem" }}>
                  Membres
                </p>
                <div style={{ maxHeight: "250px", overflow: "auto" }}>
                  {users.map((u) => (
                    <label key={u.id} style={{ display: "flex", alignItems: "center", gap: "0.5rem", padding: "0.3rem 0", fontSize: "0.9rem", cursor: "pointer" }}>
                      <input
                        type="checkbox"
                        checked={editSquad.member_ids.includes(u.id)}
                        onChange={() => toggleMember(u.id)}
                      />
                      {u.first_name} {u.last_name}
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <div className="confirm-actions" style={{ marginTop: "1rem" }}>
              <button className="btn btn-secondary" onClick={() => setEditSquad(null)}>
                Annuler
              </button>
              <button className="btn btn-primary" onClick={handleSave}>
                {editSquad.id ? "Enregistrer" : "Créer"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
